/**
 * Arriving at `/me#code=XXXX-XXXX`.
 *
 * The same shape as the parent's `family/join.ts`, and for the same reasons:
 * the code goes in the FRAGMENT so it never reaches CloudFront and cannot
 * land in an access log, and it comes out of the address bar before anything
 * renders so it is not in history, in a bookmark, or on screen over a
 * child's shoulder.
 *
 * ONE DIFFERENCE, and it is why this is a separate module rather than an
 * import. There is no sign-in round trip here -- a child has no account and
 * never leaves this origin -- so the stash exists only to carry the code
 * across a couple of React renders. It is still `sessionStorage` rather than
 * a module variable, because `/me` gets reloaded by children constantly and
 * a code that survives a reload saves them typing it again.
 *
 * WHAT MUST NEVER BE IN THAT LINK is the family key, or anything derived from
 * it. A child code carries nothing: it buys a request a parent has to
 * approve, which is the entire point of the redesign.
 */

const STASH_KEY = 'valley.child.code';

/**
 * Read a code out of the address bar, stash it, and clean up.
 *
 * Called unconditionally at start-up, before React renders. Does nothing
 * anywhere but `/me`, and never throws: a mistyped link is somebody getting a
 * text message wrong, not an error worth showing a child.
 */
export function captureChildCode(): string | null {
  if (path() !== '/me') return null;

  const fragment = window.location.hash.replace(/^#/, '');
  const code = new URLSearchParams(fragment).get('code');

  // The fragment goes either way, and before anything renders. `/me` is a
  // real page so the path stays; only the secret comes off.
  try {
    window.history.replaceState({}, '', '/me');
  } catch {
    // A browser that refuses. The stash below is what the app reads, so this
    // is cosmetic rather than load-bearing.
  }

  if (!code) return null;
  const cleaned = code.trim();
  if (!cleaned) return null;
  try {
    sessionStorage.setItem(STASH_KEY, cleaned);
  } catch {
    // Private window with storage blocked. The child types the code instead,
    // which is the screen they would have seen without a link at all.
  }
  return cleaned;
}

export function stashedChildCode(): string | null {
  try {
    return sessionStorage.getItem(STASH_KEY);
  } catch {
    return null;
  }
}

/**
 * Drop it, whether or not it worked.
 *
 * Both ways round. A stash that outlived a failure would put a child back on
 * the same doomed screen on every reload with no way out; one that outlived a
 * SUCCESS would try to add the same person again every time the page loaded.
 */
export function clearChildCode(): void {
  try {
    sessionStorage.removeItem(STASH_KEY);
  } catch {
    /* nothing to do */
  }
}

function path(): string {
  return window.location.pathname.replace(/\/+$/, '') || '/';
}
